import { useEffect, useState } from 'react';

import { Box, Divider, DropdownSelect, Text, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

import { useGetPropertiesList } from '../../../hooks/queries/properties/zrm';
import { transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';

import { ControlledSearchBox } from '../../../../srcLegacy/shared/components/ControlledSearchBox';
import { useAuth } from '../../../components/AuthProvider';

import { emitPropertyListFilterEvent, EVENT_IDS } from '../../../analytics/events/propertiesPage';

import type { Dispatch, SetStateAction } from 'react';
import type { FilterState } from '../../../utils/propertiesPageUtils';

const { PROPERTIES_SUBNAV_FILTER_OPTIONS, PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;
const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const baseHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
];

const groupedHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET,
];

const subNavFilterOptions = {
  options: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS.reduce(
    (acc, subNav) => {
      acc[subNav.id] = { ...subNav };

      return acc;
    },
    {} as Record<string, (typeof PROPERTIES_SUBNAV_FILTER_OPTIONS)[0]>,
  ),
};

type Props = {
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
};

export const Filters = ({ filters, setFilters }: Props) => {
  const { user } = useAuth();

  // Local state for immediate UI feedback
  const [searchInput, setSearchInput] = useState(filters.searchText);

  // Sync searchInput with filters.searchText when it changes from external sources
  useEffect(() => {
    setSearchInput(filters.searchText);
  }, [filters.searchText]);

  const { data: { listingCount } = {}, isPending: listingsLoading } = useGetPropertiesList(
    transformFiltersToApiParams(filters),
  );

  const {
    active: numActiveListings = 0,
    inactive: numInactiveListings = 0,
    archived: numArchivedListings = 0,
    total: numTotalListings = 0,
  } = listingCount ?? {};

  const canViewSearch = listingsLoading || (numTotalListings ?? 0) >= 5;

  const onFilterChange = (value: string | Array<string>) => {
    const selectedFilterType = Array.isArray(value) ? value[0] : value;

    // treat archived filter as a toggle between all and archived view when there are no unarchived listings
    if (
      filters.subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED &&
      selectedFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED &&
      numTotalListings === 0
    ) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        subNavFilterType: PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
        searchText: '', // Clear search when changing filters
      }));
    } else {
      setFilters((currentFilters) => ({
        ...currentFilters,
        subNavFilterType: selectedFilterType,
        searchText: '', // Clear search when changing filters
      }));
    }

    emitPropertyListFilterEvent({
      user,
      triggerObject: PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS[selectedFilterType],
    });
  };

  const onSearchFilterUpdate = (searchText: string) => {
    setFilters((currentFilters) => ({ ...currentFilters, searchText }));
  };

  const countMap = {
    [PROPERTIES_SUBNAV_FILTER_TYPE.ALL]: numTotalListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET]: numActiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET]: numInactiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED]: numArchivedListings,
  };
  PROPERTIES_SUBNAV_FILTER_OPTIONS.forEach((subNav) => {
    subNavFilterOptions.options[subNav.id].count = countMap[subNav.id] || 0;
  });

  const placeholderText = 'Search by city, address, and ZIP code';

  const renderToggleButton = (id: string, label: string, count: number, selected?: boolean) => {
    const isBaseHideableTab = baseHideableTabs.includes(id) && count === 0 && id !== filters.subNavFilterType;
    const isGroupedHideableTab = groupedHideableTabs.includes(id) && count === 0 && numTotalListings === 0;

    if (!listingsLoading && (isBaseHideableTab || isGroupedHideableTab)) {
      return null;
    }

    return (
      <ToggleButton
        key={id}
        value={id}
        disabled={listingsLoading}
        selected={selected}
        css={{ xlDown: { width: '100%' } }}
        data-eventid={EVENT_IDS.PROPERTY_LIST_FILTER}
        data-testid={`PropertiesPage-Filters-${id}`}
      >
        {label} {<Text textStyle="body-sm-bold">{`(${count})`}</Text>}
      </ToggleButton>
    );
  };

  const getDropdownOptions = () => {
    const options = [];

    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.ALL) && numTotalListings === 0)) {
      options.push({ label: `All (${numTotalListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ALL });
    }
    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET) && numActiveListings === 0)) {
      options.push({ label: `For rent (${numActiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET });
    }
    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET) && numInactiveListings === 0)) {
      options.push({ label: `Off market (${numInactiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET });
    }
    if (numArchivedListings > 0 || filters.subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) {
      options.push({ label: `Archived (${numArchivedListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED });
    }

    return options;
  };

  return (
    <Box
      data-testid="PropertiesPage-Filters"
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { base: 'column-reverse', xl: 'row' },
        marginTop: { base: '600', lg: '900' },
        gap: { base: '200', lg: '600' },
      }}
    >
      <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
        {/* Shown on lg screens and above */}
        <ToggleButtonGroup
          aria-label="Filter property list by status"
          size="sm"
          css={{ xlDown: { width: '100%' }, display: { base: 'none', lg: 'flex' } }}
          onValueChange={onFilterChange}
          value={filters.subNavFilterType}
          data-testid="PropertiesPage-Filters-AllButton"
        >
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ALL, 'All', numTotalListings)}
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET, 'For rent', numActiveListings)}
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET, 'Off market', numInactiveListings)}
        </ToggleButtonGroup>

        {/* archived toggle button */}
        {(listingsLoading ||
          numArchivedListings > 0 ||
          filters.subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) && (
          <Box css={{ display: { base: 'none', lg: 'flex' }, alignItems: 'center' }}>
            {Boolean(numTotalListings > 0 || listingsLoading) && (
              <Divider orientation="vertical" css={{ height: '900', marginInline: '400' }} />
            )}

            <Box
              aria-disabled={listingsLoading}
              onClick={() => !listingsLoading && onFilterChange(PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED)}
            >
              {renderToggleButton(
                PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
                'Archived',
                numArchivedListings,
                filters.subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Shown on md screens and below */}
      <DropdownSelect
        key={`${filters.subNavFilterType}-${numTotalListings}-${numActiveListings}-${numInactiveListings}`}
        value={filters.subNavFilterType}
        options={getDropdownOptions()}
        showLabelForValue
        css={{ width: '100%', display: { base: 'flex', lg: 'none' } }}
        onChange={onFilterChange}
        disabled={listingsLoading}
        data-eventid={EVENT_IDS.PROPERTY_LIST_FILTER}
        size="sm"
      />

      {canViewSearch && (
        <Box css={{ width: { base: '100%', xl: '50%' } }}>
          <ControlledSearchBox
            placeholder={placeholderText}
            value={searchInput}
            onChange={setSearchInput}
            onSearch={onSearchFilterUpdate}
            isSearching={listingsLoading && Boolean(filters.searchText)}
            disabled={listingsLoading}
            iconSize="sm"
          />
        </Box>
      )}
    </Box>
  );
};
